'use client';
import { ReactNode, useContext, createContext, useState, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { getCookie, setCookie } from 'cookies-next';
import {
  ACTIVE_ANNOUNCEMENT,
  bannerStateForPage,
  cookieOnDismiss,
  isAnnouncementTarget,
  type BannerCookieWrite,
} from '@/components/Banner/announcement';

type BannerContextType = {
  isBannerOpen: boolean;
  /** False after a route change onto/off the announced page (until the visitor closes the bar):
   * the bar then opens/closes without the slide. */
  animateBanner: boolean;
  /** True once the dismiss cookie has been read after this page load (hydration): until then the
   * bar is closed whatever the cookie says. Stays true across client-side route changes. */
  bannerReady: boolean;
  handleCloseBanner: () => void;
};

const BannerContext = createContext<BannerContextType>({} as BannerContextType);

type BannerProviderProps = {
  children: ReactNode;
};

const BannerProvider = ({ children }: BannerProviderProps) => {
  // Site-wide announcement banner; the campaign comes from Banner/announcement.ts.
  // The banner is non-sticky and scrolls away above the sticky MegaNav, so the
  // header offset stays nav-height in both states (see --totalHeadersOffset in
  // global.css). Closing it, or (per campaign) visiting the announced page, is remembered
  // in the campaign's dismiss cookie below.
  const bannerEnabled = true;

  // Starts closed and animates open after hydration (the effect below), pushing the page
  // down by the banner height. That's a layout shift on every page load for visitors who
  // haven't dismissed it. Measured Oct 2026 (cold load, Fast 4G, 4× CPU): CLS 0.051 at
  // 375px, 0.023 at 1440px, both within Google's "good" range (< 0.1). Deemed acceptable:
  // the reveal is smooth and the banner is temporary. Rendering it open from the server
  // was tried and dropped (#3735).
  const [isBannerShown, setIsBannerShown] = useState(false);
  const [bannerReady, setBannerReady] = useState(false);

  // Hidden on the page it announces (it would link the page to itself); for campaigns with
  // hideAfterVisitDays, visiting that page also hides it elsewhere (effect below). Closed on the
  // server and on first render either way, so this can't cause a hydration mismatch.
  const pathname = usePathname();
  const isOnAnnouncedPage = isAnnouncementTarget(pathname, ACTIVE_ANNOUNCEMENT);
  const isBannerOpen = isBannerShown && !isOnAnnouncedPage;

  // A client-side route change onto or off the announced page opens/closes the bar instantly:
  // sliding it would shift the whole page (MegaNav included) after the navigation. Set in the
  // same render as the flip, so the transition is off when max-height changes. The initial open
  // (no flip yet) and the visitor's close (re-enables it) keep the slide.
  const [animateBanner, setAnimateBanner] = useState(true);
  const [wasOnAnnouncedPage, setWasOnAnnouncedPage] = useState(isOnAnnouncedPage);
  if (wasOnAnnouncedPage !== isOnAnnouncedPage) {
    setWasOnAnnouncedPage(isOnAnnouncedPage);
    setAnimateBanner(false);
  }

  // Campaign-versioned so a new announcement isn't suppressed by a stale dismiss cookie from a
  // previous banner. How long a close or a visit hides the bar is per campaign too
  // (dismissDays / hideAfterVisitDays in Banner/announcement.ts).
  const announcement = ACTIVE_ANNOUNCEMENT;
  const dismissCookie = announcement.dismissCookie;
  // Last value written this session: the fallback when cookies can't be read or written (blocked,
  // or document.cookie throws, e.g. in a sandboxed iframe).
  const writtenThisSession = useRef<string | undefined>(undefined);
  const readDismissCookie = () => {
    try {
      return getCookie(dismissCookie) ?? writtenThisSession.current;
    } catch {
      return writtenThisSession.current;
    }
  };

  const writeDismissCookie = (write: BannerCookieWrite | null) => {
    if (!write) return;
    writtenThisSession.current = write.value;
    try {
      setCookie(dismissCookie, write.value, { maxAge: write.maxAgeSeconds });
    } catch {
      // Kept for this session only (writtenThisSession).
    }
  };

  const handleCloseBanner = () => {
    setAnimateBanner(true);
    setIsBannerShown(false);
    writeDismissCookie(cookieOnDismiss(announcement, readDismissCookie()));
  };

  useEffect(() => {
    if (!bannerEnabled) return;
    const { show, write } = bannerStateForPage(pathname, announcement, readDismissCookie());
    // A visit, not a click on the bar: no Fathom event here.
    writeDismissCookie(write);
    setIsBannerShown(show);
    setBannerReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const value = { isBannerOpen, animateBanner, bannerReady, handleCloseBanner };
  return <BannerContext.Provider value={value}>{children}</BannerContext.Provider>;
};

const useBannerState = () => {
  const context = useContext(BannerContext);
  if (context === undefined) {
    throw new Error('useBannerState must be used from within a BannerProvider');
  }
  return context;
};

export { BannerProvider, useBannerState };
